import { Fragment, memo } from 'react';
import Loadable from 'UIComponents/decorators/Loadable';
import styled from 'styled-components';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIButtonGroup from 'UIComponents/button/UIButtonGroup';
import UIFloatingButtonGroup from 'UIComponents/button/UIFloatingButtonGroup';
import I18n from 'I18n';
import { getFileId, getFileMetadata
// @ts-expect-error resolvedAttachmentGetters is not typed
} from 'conversations-internal-schema/resolved-attachment/operators/resolvedAttachmentGetters';
import { getSignedUrl } from 'conversations-internal-schema/file-metadata/operators/getSignedUrl';
import { getType, getUrl
// @ts-expect-error fileMetadataGetters is not typed
} from 'conversations-internal-schema/file-metadata/operators/fileMetadataGetters';
import { AUDIO, IMG, MOVIE } from 'conversations-message-history/common-message-format/constants/fileAttachmentTypes';
import { usageTracker } from '../usage-tracker/usageTracker';
import { downloadEventConfig, downloadActionConfig } from '../usage-tracker/threadInteraction';
import { getUsageTrackingChannel } from '../usage-tracker/trackerChannelIdMapping';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const InlineAudioAttachment = Loadable({
  loader: () => import('./InlineAudioAttachment').then(module => module.default),
  LoadingComponent: () => null
});
const InlineImageAttachment = Loadable({
  loader: () => import('./InlineImageAttachment').then(module => module.default),
  LoadingComponent: () => null
});
const InlineVideoAttachment = Loadable({
  loader: () => import('./InlineVideoAttachment').then(module => module.default),
  LoadingComponent: () => null
});
const MAX_ATTACHMENT_WIDTH = 300;
const FileAttachmentWrapper = styled.div.withConfig({
  displayName: "InlineAttachment__FileAttachmentWrapper",
  componentId: "sc-13ln6t0-0"
})(["position:relative;margin:10px 0;"]);
const StyledFloatingButtonGroup = styled(UIFloatingButtonGroup).withConfig({
  displayName: "InlineAttachment__StyledFloatingButtonGroup",
  componentId: "sc-13ln6t0-1"
})([".UIButtonGroup{top:0;right:0;}"]);
const AttachmentConfigs = {
  [IMG]: {
    component: InlineImageAttachment,
    hasHoverOptions: true,
    hasMaxWidth: true
  },
  [MOVIE]: {
    component: InlineVideoAttachment,
    hasHoverOptions: true,
    hasMaxWidth: true
  },
  [AUDIO]: {
    component: InlineAudioAttachment,
    hasHoverOptions: false,
    hasMaxWidth: false
  }
};
const InlineAttachmentWithOptions = ({
  fileAttachment,
  genericChannelId
}) => {
  const fileMetadata = getFileMetadata(fileAttachment);
  const fileId = getFileId(fileAttachment);
  const type = getType(fileMetadata);
  const url = getUrl(fileMetadata);
  const downloadUrl = getSignedUrl({
    fileId,
    isDownloadRequest: true
  });
  const AttachmentConfig = AttachmentConfigs[type];
  const AttachmentComponent = AttachmentConfig && AttachmentConfig['component'];
  const hasHoverOptions = AttachmentConfig && AttachmentConfig['hasHoverOptions'];
  const hasMaxWidth = AttachmentConfig && AttachmentConfig['hasMaxWidth'];
  const Attachment = /*#__PURE__*/_jsx(AttachmentComponent, {
    fileAttachment: fileAttachment,
    maxWidth: MAX_ATTACHMENT_WIDTH,
    genericChannelId: genericChannelId
  });
  const usageDownloadEvent = downloadEventConfig[type];
  const usageDownloadAction = downloadActionConfig[type];
  const handleDownload = () => {
    usageTracker.track(usageDownloadEvent, {
      action: usageDownloadAction,
      channel: getUsageTrackingChannel(genericChannelId)
    });
  };
  const handleClickAttachment = () => {
    usageTracker.track('click-attachment', {
      action: 'click attachment',
      channel: getUsageTrackingChannel(genericChannelId)
    });
  };
  return /*#__PURE__*/_jsx(FileAttachmentWrapper, {
    "data-test-id": "file-attachment-wrapper",
    style: {
      width: hasMaxWidth ? 'fit-content' : '100%',
      maxWidth: hasMaxWidth ? MAX_ATTACHMENT_WIDTH : 'none'
    },
    children: hasHoverOptions ? /*#__PURE__*/_jsx(StyledFloatingButtonGroup, {
      buttonGroup: /*#__PURE__*/_jsxs(UIButtonGroup, {
        children: [/*#__PURE__*/_jsx(UIIconButton, {
          href: downloadUrl,
          download: true,
          onClick: handleDownload,
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "download",
            screenReaderText: I18n.text('conversations-inbox-message-history.attachments.download')
          })
        }), /*#__PURE__*/_jsx(UIIconButton, {
          href: url,
          target: "_blank",
          onClick: handleClickAttachment,
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "externalLink",
            screenReaderText: I18n.text('conversations-inbox-message-history.attachments.openInNewWindow')
          })
        })]
      }),
      children: Attachment
    }) : /*#__PURE__*/_jsx(Fragment, {
      children: Attachment
    })
  });
};
InlineAttachmentWithOptions.displayName = 'InlineAttachmentWithOptions';
export default /*#__PURE__*/memo(InlineAttachmentWithOptions);